'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { HouseholdSwitcherMembership } from './HouseholdSwitcher';
import { useFlagsAndLoadingState } from '../../contexts/launch-darkly-flags-ready-context';
import { SwitchActiveHouseholdMutation, ViewerHouseholdsQuery } from './graphql';
import {
  clearLastAccessedHouseholdId,
  HOUSEHOLD_URL_PARAM,
  readLastAccessedHouseholdId,
  writeLastAccessedHouseholdId,
} from './household-hint-storage';

type SwitchCache = { readonly evict: (options: { readonly id: string }) => void; readonly gc: () => void };

export const switchActiveHouseholdMutationOptions = {
  refetchQueries: [{ query: ViewerHouseholdsQuery }],
  awaitRefetchQueries: true,
  update: (cache: SwitchCache) => {
    cache.evict({ id: 'ROOT_QUERY' });
    cache.gc();
  },
};

export type UseHouseholdSwitcherResult = {
  readonly ready: boolean;
  readonly activeHouseholdId: string | null;
  readonly memberHouseholds: readonly HouseholdSwitcherMembership[];
  readonly onSelect: (householdId: string) => void;
  readonly switching: boolean;
};

export function useHouseholdSwitcher(): UseHouseholdSwitcherResult {
  const router = useRouter();
  const { flagsReady } = useFlagsAndLoadingState();
  const { data, loading } = useQueryWithErrorLogging(ViewerHouseholdsQuery, { skip: !flagsReady });

  const [switchActiveHousehold, { loading: switching }] = useMutationWithErrorLogging(
    SwitchActiveHouseholdMutation,
    switchActiveHouseholdMutationOptions,
  );

  const writeHouseholdIdToUrl = useCallback(
    (householdId: string) => {
      const currentQueryId = router.query[HOUSEHOLD_URL_PARAM];
      if (currentQueryId === householdId) {
        return;
      }
      void router.replace(
        { pathname: router.pathname, query: { ...router.query, [HOUSEHOLD_URL_PARAM]: householdId } },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  const onSelect = useCallback(
    (householdId: string) => {
      writeLastAccessedHouseholdId(householdId);
      writeHouseholdIdToUrl(householdId);
      void switchActiveHousehold({ variables: { input: { householdId } } });
    },
    [switchActiveHousehold, writeHouseholdIdToUrl],
  );

  const reconciledIdRef = useRef<string | null>(null);
  const viewer = data?.currentUser.user ?? null;
  const activeHouseholdId = viewer?.activeHousehold?.id ?? null;
  const memberHouseholds = viewer?.memberHouseholds ?? null;

  useEffect(() => {
    if (!viewer || !memberHouseholds || memberHouseholds.length <= 1) {
      // Single-household viewers don't need URL/storage reconciliation: the
      // backend's current_household already returns their only household as
      // activeHousehold, and there's nothing to switch to.
      return;
    }

    const memberIds = new Set(memberHouseholds.map((h) => h.id));

    const rawQueryId = router.query[HOUSEHOLD_URL_PARAM];
    const queryId = typeof rawQueryId === 'string' ? rawQueryId : null;
    const storedId = readLastAccessedHouseholdId();

    const validQueryId = queryId && memberIds.has(queryId) ? queryId : null;
    const validStoredId = storedId && memberIds.has(storedId) ? storedId : null;

    if (storedId && !validStoredId) {
      clearLastAccessedHouseholdId();
    }

    const desired = validQueryId ?? validStoredId;

    if (desired && desired !== activeHouseholdId && reconciledIdRef.current !== desired) {
      reconciledIdRef.current = desired;
      writeLastAccessedHouseholdId(desired);
      writeHouseholdIdToUrl(desired);
      void switchActiveHousehold({ variables: { input: { householdId: desired } } });
      return;
    }

    if (!desired && activeHouseholdId) {
      writeLastAccessedHouseholdId(activeHouseholdId);
      writeHouseholdIdToUrl(activeHouseholdId);
    }
  }, [viewer, memberHouseholds, activeHouseholdId, router.query, switchActiveHousehold, writeHouseholdIdToUrl]);

  const ready = flagsReady && !loading && !!viewer;

  return {
    ready,
    activeHouseholdId,
    memberHouseholds: viewer?.memberHouseholds ?? [],
    onSelect,
    switching,
  };
}
